import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';

interface Kamoku {
    id: number;
    kamoku_code: string;
    kamoku: string;
    kamoku_yomi?: string;
    hojo_kamoku_ari?: boolean; // 補助科目あり
}

interface HojoKamoku {
    id: number;
    kamoku_id: number;
    hojo_code: string;
    hojo_name: string;
    kamoku_name?: string;
}

interface KamokuSelectWithHojoProps {
    kamokuList: Kamoku[];
    value: number | null;
    hojoValue?: number | null; // 補助科目ID
    onChange: (kamokuId: string, hojoId?: string) => void;
    onFocus?: () => void;
    onBlur?: () => void;
    onKeyDown?: (e: React.KeyboardEvent) => void;
    style?: React.CSSProperties;
    placeholder?: string;
    autoFocus?: boolean;
    apiFetch?: (url: string, options?: any) => Promise<any>;
    currentTenantId?: number;
}

const KamokuSelectWithHojo: React.FC<KamokuSelectWithHojoProps> = ({
    kamokuList,
    value,
    hojoValue,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    style,
    placeholder = '選択してください',
    autoFocus = false,
    apiFetch,
    currentTenantId,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    // 補助科目関連state
    const [hojoList, setHojoList] = useState<HojoKamoku[]>([]);
    const [showHojoSelect, setShowHojoSelect] = useState(false);
    const [selectedKamoku, setSelectedKamoku] = useState<Kamoku | null>(null);

    // 補助科目を取得
    const fetchHojoList = async (kamokuId: number) => {
        if (!apiFetch) return;
        try {
            const tenantParam = currentTenantId ? `?tenant_id=${currentTenantId}` : '';
            const response = await apiFetch(`/api/hojo-kamoku/by-kamoku/${kamokuId}${tenantParam}`);
            if (Array.isArray(response)) {
                setHojoList(response);
                if (response.length > 0) {
                    setShowHojoSelect(true);
                }
            }
        } catch (error) {
            console.error('補助科目取得エラー:', error);
            setHojoList([]);
        }
    };

    // 初期値の設定
    useEffect(() => {
        if (value !== null) {
            const selected = kamokuList.find((k) => k.id === value);
            if (selected) {
                setSelectedKamoku(selected);
                if (!isOpen) {
                    let displayText = `${selected.kamoku_code} - ${selected.kamoku}`;
                    // 補助科目が選択されている場合は表示に含める
                    if (hojoValue && hojoList.length > 0) {
                        const hojo = hojoList.find(h => h.id === hojoValue);
                        if (hojo) {
                            displayText += ` [${hojo.hojo_name}]`;
                        }
                    }
                    setSearchTerm(displayText);
                }
                // 補助科目ありの場合は取得
                if (selected.hojo_kamoku_ari && apiFetch) {
                    fetchHojoList(selected.id);
                }
            }
        } else if (!isOpen) {
            setSearchTerm('');
            setSelectedKamoku(null);
            setHojoList([]);
            setShowHojoSelect(false);
        }
    }, [value, kamokuList, isOpen, hojoValue, hojoList.length]);

    const filteredOptions = useMemo(() => {
        if (!searchTerm) return kamokuList;
        const lowerSearch = searchTerm.toLowerCase();

        return kamokuList.filter((k) => {
            const code = k.kamoku_code.toLowerCase();
            const name = k.kamoku.toLowerCase();
            const yomi = (k.kamoku_yomi || '').toLowerCase();
            const label = `${k.kamoku_code} - ${k.kamoku}`.toLowerCase();

            return (
                code.includes(lowerSearch) ||
                name.includes(lowerSearch) ||
                yomi.includes(lowerSearch) ||
                label.includes(lowerSearch)
            );
        });
    }, [kamokuList, searchTerm]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setIsOpen(true);
        setHighlightedIndex(0);
        if (e.target.value === '') {
            onChange('');
            setHojoList([]);
            setShowHojoSelect(false);
        }
    };

    const handleSelect = async (kamoku: Kamoku) => {
        setSelectedKamoku(kamoku);

        if (kamoku.hojo_kamoku_ari && apiFetch) {
            // 補助科目ありの場合は、補助科目を取得して表示
            await fetchHojoList(kamoku.id);
            setSearchTerm(`${kamoku.kamoku_code} - ${kamoku.kamoku}`);
            setIsOpen(false);
            // 親科目を選択し、補助科目選択を待つ
            onChange(kamoku.id.toString());
        } else {
            // 補助科目なしの場合は直接選択完了
            onChange(kamoku.id.toString());
            setSearchTerm(`${kamoku.kamoku_code} - ${kamoku.kamoku}`);
            setIsOpen(false);
            setShowHojoSelect(false);
        }
    };

    const handleHojoSelect = (hojo: HojoKamoku) => {
        if (selectedKamoku) {
            onChange(selectedKamoku.id.toString(), hojo.id.toString());
            setSearchTerm(`${selectedKamoku.kamoku_code} - ${selectedKamoku.kamoku} [${hojo.hojo_name}]`);
            setShowHojoSelect(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === ' ') {
                setIsOpen(true);
                e.preventDefault();
            } else if (onKeyDown) {
                // Enter, Tab, etc. propogate to parent handler
                onKeyDown(e);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex((prev) =>
                    prev < filteredOptions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
                break;
            case 'Enter':
                e.preventDefault();
                if (filteredOptions.length > 0) {
                    handleSelect(filteredOptions[highlightedIndex]);
                    if (onKeyDown) onKeyDown(e); // Trigger navigation
                }
                break;
            case 'Escape':
                setIsOpen(false);
                break;
            case 'Tab':
                setIsOpen(false);
                if (onKeyDown) onKeyDown(e);
                break;
            default:
                if (onKeyDown) onKeyDown(e);
                break;
        }
    };

    // Calculate dropdown position when opened
    useEffect(() => {
        if (isOpen && inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: Math.max(350, rect.width),
            });
        }
    }, [isOpen]);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Scroll highlighted item into view
    useEffect(() => {
        if (isOpen && listRef.current) {
            const activeItem = listRef.current.children[highlightedIndex] as HTMLElement;
            if (activeItem) {
                const listTop = listRef.current.scrollTop;
                const listHeight = listRef.current.clientHeight;
                const itemTop = activeItem.offsetTop;
                const itemHeight = activeItem.clientHeight;

                if (itemTop < listTop) {
                    listRef.current.scrollTop = itemTop;
                } else if (itemTop + itemHeight > listTop + listHeight) {
                    listRef.current.scrollTop = itemTop + itemHeight - listHeight;
                }
            }
        }
    }, [highlightedIndex, isOpen]);

    return (
        <div
            ref={containerRef}
            style={{ ...style, position: 'relative', display: 'inline-block' }}
        >
            <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={handleInputChange}
                onClick={() => {
                    setIsOpen(true);
                }}
                onFocus={() => {
                    setIsOpen(true);
                    if (onFocus) onFocus();
                }}
                onBlur={() => {
                    if (onBlur) onBlur();
                }}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                style={{
                    width: '100%',
                    height: '100%',
                    boxSizing: 'border-box',
                    padding: '4px 4px',
                    border: '2px inset #c0c0c0',
                    fontFamily: 'MS UI Gothic, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                }}
                autoFocus={autoFocus}
            />

            {/* 勘定科目ドロップダウン */}
            {isOpen && filteredOptions.length > 0 && createPortal(
                <ul
                    ref={listRef}
                    style={{
                        position: 'fixed',
                        top: `${dropdownPosition.top}px`,
                        left: `${dropdownPosition.left}px`,
                        width: `${dropdownPosition.width}px`,
                        maxHeight: '300px',
                        overflowY: 'auto',
                        margin: 0,
                        padding: 0,
                        listStyle: 'none',
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        zIndex: 99999,
                        boxShadow: '2px 2px 10px rgba(0,0,0,0.3)',
                    }}
                >
                    {filteredOptions.map((kamoku, index) => (
                        <li
                            key={kamoku.id}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                handleSelect(kamoku);
                            }}
                            onMouseEnter={() => setHighlightedIndex(index)}
                            style={{
                                padding: '8px 10px',
                                cursor: 'pointer',
                                backgroundColor: index === highlightedIndex ? '#0066cc' : 'white',
                                color: index === highlightedIndex ? 'white' : 'black',
                                fontSize: '14px',
                                fontFamily: 'MS UI Gothic, sans-serif',
                                lineHeight: '1.4',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <span>
                                {kamoku.kamoku_code} - {kamoku.kamoku}
                                {kamoku.kamoku_yomi && (
                                    <span style={{
                                        fontSize: '11px',
                                        marginLeft: '8px',
                                        color: index === highlightedIndex ? '#e0e0e0' : '#888'
                                    }}>
                                        ({kamoku.kamoku_yomi})
                                    </span>
                                )}
                            </span>
                            {kamoku.hojo_kamoku_ari && (
                                <span style={{
                                    fontSize: '10px',
                                    backgroundColor: index === highlightedIndex ? '#fff' : '#0066cc',
                                    color: index === highlightedIndex ? '#0066cc' : '#fff',
                                    padding: '2px 6px',
                                    borderRadius: '3px',
                                    marginLeft: '8px',
                                }}>
                                    補助
                                </span>
                            )}
                        </li>
                    ))}
                </ul>,
                document.body
            )}

            {/* 補助科目選択ドロップダウン */}
            {showHojoSelect && hojoList.length > 0 && !isOpen && createPortal(
                <div
                    style={{
                        position: 'fixed',
                        top: `${dropdownPosition.top}px`,
                        left: `${dropdownPosition.left}px`,
                        width: `${dropdownPosition.width}px`,
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        zIndex: 99999,
                        boxShadow: '2px 2px 10px rgba(0,0,0,0.3)',
                    }}
                >
                    <div style={{
                        padding: '8px 10px',
                        backgroundColor: '#f0f0f0',
                        borderBottom: '1px solid #ccc',
                        fontSize: '12px',
                        fontWeight: 'bold',
                    }}>
                        補助科目を選択（任意）
                    </div>
                    <ul
                        style={{
                            maxHeight: '200px',
                            overflowY: 'auto',
                            margin: 0,
                            padding: 0,
                            listStyle: 'none',
                        }}
                    >
                        <li
                            onMouseDown={(e) => {
                                e.preventDefault();
                                if (selectedKamoku) {
                                    onChange(selectedKamoku.id.toString());
                                    setSearchTerm(`${selectedKamoku.kamoku_code} - ${selectedKamoku.kamoku}`);
                                    setShowHojoSelect(false);
                                }
                            }}
                            style={{
                                padding: '8px 10px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontStyle: 'italic',
                                color: '#666',
                                borderBottom: '1px solid #eee',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                            （補助科目なしで登録）
                        </li>
                        {hojoList.map((hojo) => (
                            <li
                                key={hojo.id}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleHojoSelect(hojo);
                                }}
                                style={{
                                    padding: '8px 10px',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    fontFamily: 'MS UI Gothic, sans-serif',
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e6f0ff'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                            >
                                <span style={{ fontWeight: 'bold', marginRight: '8px' }}>
                                    {hojo.hojo_code}
                                </span>
                                {hojo.hojo_name}
                            </li>
                        ))}
                    </ul>
                </div>,
                document.body
            )}
        </div>
    );
};

export default KamokuSelectWithHojo;
